<template>
  <div class="purchase-details fade-in">
    <!-- Header Section -->
    <ProductDetailsHeader
        title="Purchase Details"
        icon="cart-plus"
        :main="headerMain"
        :files="purchase.files"
    >
      <template #header-actions>
        <div class="d-flex gap-2 align-items-center">
          <button
              v-if="purchase.status === 'submitted' && purchase.invoice === null"
              class="btn btn-success"
              @click="addInvoiceModal = true;">
            <i class="bi bi-file-earmark-plus me-1"></i> Add Invoice
          </button>

          <button
              class="btn btn-primary"
              @click="openAddPaymentModal">
            <i class="bi bi-credit-card me-1"></i> Add Payment
          </button>

          <div v-if="purchase.status !== 'complete' && purchaseProducts.data.length > 0">
            <select
                class="form-select status-select w-auto"
                v-model="purchaseStatus"
                @change="confirmDialog(
                    getConfirmTitle,
                    updatePurchaseStatus,
                    'Are you sure you want to change purchase status to ' + purchaseStatus
                )" data="">
              <option value="" disabled selected>Change Status</option>
              <option v-if="purchase.status === 'pending'" value="submitted" class="text-primary fs-5">Submit Order</option>
              <option v-if="purchase.status === 'submitted'" value="pending" class="text-warning fs-5">Edit Order</option>
              <option v-if="purchase.status === 'submitted'" value="complete" class="text-success fs-5">Approve Order</option>
            </select>
          </div>
        </div>
      </template>
    </ProductDetailsHeader>

    <!-- Purchase Products Section -->
    <ProductDetailsPages
        title="Purchase"
        :loading="loading"
        :detail-products="purchaseProducts"
        :current-page="detailsPage"
        :current-per-page="detailsPerPage"
        :current-search="detailsSearch"
        @update:page="onDetailsPageChange"
        @update:per-page="onDetailsPerPageChange"
        @update:search="onDetailsSearchChange"
        :main="headerMain"
        @edit-product="populatePurchaseEdit"
        @delete-product="deletePurchaseProduct"
    >
      <template #footer-actions>
        <button
            v-if="purchase.status === 'submitted' && purchase.invoice === null"
            class="btn btn-success"
            @click="addInvoiceModal = true;">
          <i class="bi bi-file-earmark-plus me-1"></i> Add Invoice
        </button>
        <button
            class="btn btn-primary"
            @click="openAddPaymentModal">
          <i class="bi bi-credit-card me-1"></i> Add Payment
        </button>
        <select
            v-if="purchase.status !== 'complete' && purchaseProducts.data.length > 0"
            class="form-select status-select w-auto"
            v-model="purchaseStatus"
            @change="confirmDialog(
                getConfirmTitle,
                updatePurchaseStatus,
                'Are you sure you want to change purchase status to ' + purchaseStatus
            )" data="">
          <option value="" disabled selected>Change Status</option>
          <option v-if="purchase.status === 'pending'" value="submitted" class="text-primary fs-5">Submit Order</option>
          <option v-if="purchase.status === 'submitted'" value="pending" class="text-warning fs-5">Edit Order</option>
          <option v-if="purchase.status === 'submitted'" value="complete" class="text-success fs-5">Approve Order</option>
        </select>
      </template>
    </ProductDetailsPages>

    <!-- Add Invoice Modal -->
    <Modal :open="addInvoiceModal" @closed="addInvoiceModal = false">
      <div class="modal-header">
        <h5 class="modal-title m-0">
          <i class="bi bi-file-earmark-plus me-2 text-primary"></i>Add Supplier Invoice
        </h5>
        <button @click="addInvoiceModal = false" type="button" class="btn-close outline-none" data-dismiss="modal" aria-label="Close">
        </button>
      </div>
      <div class="modal-body">
        <form @submit.prevent="">
          <div class="row">
            <div class="form-group col-md-12">
              <label for="name" class="form-label fw-medium mb-2">Invoice No:</label>
              <input type="text" class="form-control" v-model="invoice.invoice" required>
            </div>
            <div class="form-group col-md-12 mt-3">
              <label for="quantity" class="form-label fw-medium mb-2">Invoice File:</label>
              <input class="form-control" type="file" @input="handleFileUpload" name="docs" accept=".jpeg,.png,.jpg,.pdf">
            </div>
          </div>
        </form>
      </div>
      <div class="modal-footer">
        <button type="button" class="btn btn-outline-secondary" data-dismiss="modal" @click="addInvoiceModal = false">Cancel</button>
        <button type="button" class="btn btn-primary" @click="addInvoice">
          <i class="bi bi-plus-lg me-1"></i> Add Invoice
        </button>
      </div>
    </Modal>

    <!-- Add/Edit Product Modal -->
    <Modal :open="addProductModal" @closed="addProductModal = false" dialog-size="modal-lg">
      <div class="modal-header">
        <h5 class="modal-title m-0" v-if="addProductMode === 'add'">
          <i class="bi bi-plus-circle me-2 text-primary"></i>Add Product to Purchase
        </h5>
        <h5 class="modal-title m-0" v-else>
          <i class="bi bi-pencil-square me-2 text-primary"></i>Edit Purchase Product
        </h5>
        <button @click="addProductModal = false" type="button" class="btn-close outline-none" data-dismiss="modal" aria-label="Close">
        </button>
      </div>
      <div class="modal-body">
        <form @submit.prevent="">
          <div class="row">
            <div class="form-group col-md-12">
              <label for="name" class="form-label fw-medium mb-2">Product Name:</label>
              <input type="text" class="form-control" :value="selectedProduct.name" disabled>
            </div>
            <div class="form-group col-md-6 mt-3">
              <label for="quantity" class="form-label fw-medium mb-2">Quantity:</label>
              <input type="number" class="form-control" v-model="purchaseDetails.quantity" min="1">
            </div>
            <div class="form-group col-md-6 mt-3">
              <label for="price" class="form-label fw-medium mb-2">Buying Price:</label>
              <input type="number" class="form-control" v-model="purchaseDetails.price" min="0" step="0.01">
            </div>
            <div class="form-group col-md-6 mt-3">
              <label for="sell" class="form-label fw-medium mb-2">Selling Price:</label>
              <input type="number" class="form-control" v-model="purchaseDetails.sell" min="0" step="0.01">
            </div>
            <div class="form-group col-md-6 mt-3">
              <label for="expiry_date" class="form-label fw-medium mb-2">Expiry Date: <span class="text-muted fst-italic small">(optional)</span></label>
              <input type="date" class="form-control" v-model="purchaseDetails.expiry_date">
            </div>
            <div class="form-group col-md-12 mt-3">
              <label for="comment" class="form-label fw-medium mb-2">Comment: <span class="text-muted fst-italic small">(optional)</span></label>
              <input type="text" class="form-control" v-model="purchaseDetails.comment" placeholder="Add any special instructions or notes">
            </div>
          </div>
        </form>
      </div>
      <div class="modal-footer">
        <button type="button" class="btn btn-outline-secondary" data-dismiss="modal" @click="addProductModal = false">Cancel</button>
        <button type="button" class="btn btn-primary" v-if="addProductMode === 'add'" @click="addPurchaseProduct">
          <i class="bi bi-plus-lg me-1"></i> Add Product
        </button>
        <button type="button" class="btn btn-primary" v-else @click="updatePurchaseProduct(purchaseDetails.id)">
          <i class="bi bi-check-lg me-1"></i> Update Product
        </button>
      </div>
    </Modal>

    <!-- Add Payment Modal -->
    <AddPaymentModal
      :open="paymentModal"
      :mode="paymentMode"
      :payment="payment"
      :preload-supplier-id="purchase.supplier_id"
      :preload-purchase-id="$route.params.id"
      :hide-supplier-id="true"
      :hide-purchase-id="true"
      @closed="closePaymentModal"
      @submit="handlePaymentSubmit"
    />
  </div>
</template>

<script>
import ProductDetailsHeader from "@/components/details/ProductDetailsHeader.vue";
import ProductDetailsPages from "@/components/details/ProductDetailsPages.vue";
import StoreProducts from "@/components/store/StoreProducts.vue";
import purchaseService from "@/api/purchaseService";
import supplierPaymentService from "@/api/supplierPaymentService";
import confirmDialog  from "@/utils/confirmDialog";
import notify from "@/utils/notify";
import AddPaymentModal from "@/components/modal/AddPaymentModal.vue";

export default {
  name: "PurchaseDetails",
  components: {ProductDetailsHeader, ProductDetailsPages, StoreProducts, AddPaymentModal},

  data(){
    return {
      purchaseStatus: '',
      purchase: {
        supplier: {},
        user: {},
      },
      invoice: {
        purchase_id: this.$route.params.id,
        invoice: '',
        file: '',
      },
      purchaseDetails: {
        purchase_id: this.$route.params.id,
        product_id: '',
        quantity: 1,
        price: '',
        sell: '',
        expiry_date: '',
        comment: '',
      },
      selectedProduct: {
        product: {},
        category: {}
      },
      purchaseProducts: { data: [] },
      detailsPage: 1,
      detailsPerPage: 20,
      detailsSearch: '',
      loading: true,
      addInvoiceModal: false,
      addProductModal: false,
      addProductMode: 'add',
      selectedFile: null,
      paymentModal: false,
      paymentMode: 'add',
      payment: {
        organization_id: '',
        purchase_id: '',
        supplier_id: '',
        user_id: '',
        code: '',
        amount: '',
        payment_method: '',
        reference: '',
        status: 'pending',
        due_date: '',
        payment_date: null,
        notes: ''
      },
    }
  },

  methods:{
    confirmDialog,
    async getPurchaseDetails() {
      this.purchaseProducts = await purchaseService.getPurchaseDetails(this.$route.params.id, {
        page: this.detailsPage,
        per_page: this.detailsPerPage
      })
      this.loading = false
    },
    onDetailsPageChange(page){
      this.detailsPage = page;
      this.getPurchaseDetails();
    },
    onDetailsPerPageChange(perPage){
      this.detailsPerPage = perPage;
      this.detailsPage = 1;
      this.getPurchaseDetails();
    },
    onDetailsSearchChange(search){
      this.detailsSearch = search;
      this.detailsPage = 1;
      this.getPurchaseDetails();
    },
    populateAddProduct(storeProduct) {
      this.selectedProduct.name = storeProduct.product.name
      this.selectedProduct.category = storeProduct.product.category || {}
      this.purchaseDetails.product_id = storeProduct.product_id
      this.purchaseDetails.price = storeProduct.price
      this.addProductModal = true;
      this.addProductMode = 'add';
    },
    async addPurchaseProduct() {
      let response = await purchaseService.createPurchaseDetails(this.purchaseDetails)
      if (response) {
        this.addProductModal = false
        await this.getPurchaseDetails()
      }
    },
    async updatePurchaseStatus() {
      if (this.purchase.invoice === null && this.purchaseStatus === 'complete') {
        notify.alert.info('Please add invoice before approving purchase')
      } else {
        await purchaseService.changePurchaseStatus({status: this.purchaseStatus}, this.$route.params.id)
      }
    },
    async updatePurchaseProduct() {
      let response = await purchaseService.updatePurchaseDetails(this.purchaseDetails, this.purchaseDetails.id)
      if (response) {
        this.addProductModal = false
        await this.getPurchaseDetails()
      }
    },
    populatePurchaseEdit(purchaseItem) {
      this.purchaseDetails.id = purchaseItem.id
      this.selectedProduct.name = purchaseItem.product.name
      this.selectedProduct.category = purchaseItem.product.category || {}
      this.purchaseDetails.product_id = purchaseItem.product_id
      this.purchaseDetails.price = purchaseItem.price
      this.purchaseDetails.sell = purchaseItem.sell
      this.purchaseDetails.quantity = purchaseItem.quantity
      this.purchaseDetails.expiry_date = purchaseItem.expiry_date
      this.purchaseDetails.comment = purchaseItem.comment
      this.addProductModal = true;
      this.addProductMode = 'edit';
    },
    async deletePurchaseProduct(id) {
      let response = await purchaseService.deletePurchaseDetails(id)
      if (response) {
        await this.getPurchaseDetails()
      }
    },
    handleFileUpload(file) {
      this.selectedFile = file
    },
    addInvoice : async function() {
      if (this.selectedFile === null) {
        notify.alert.error('Please select a file')
      } else {
        let fileReader = this.processFile(this.selectedFile)
        fileReader.onload = async () => {
          if (typeof fileReader.result === 'string')
            this.invoice.file = fileReader.result
          let response = await purchaseService.addSupplierInvoice(this.invoice)
          if (response)
            window.location.reload()
        }
      }
    },
    processFile : function(file) {
      const fileReader = new FileReader()
      const { files } = file.target
      if (files && files.length) {
        fileReader.readAsDataURL(files[0])
        return fileReader
      }
    },
    openAddPaymentModal() {
      this.paymentMode = 'add';
      this.payment = {
        organization_id: '',
        purchase_id: '',
        supplier_id: '',
        user_id: '',
        code: '',
        amount: '',
        payment_method: '',
        reference: '',
        status: 'pending',
        due_date: '',
        payment_date: null,
        notes: ''
      };
      this.paymentModal = true;
    },
    closePaymentModal() {
      this.paymentModal = false;
      this.paymentMode = 'add';
      this.payment = {
        organization_id: '',
        purchase_id: '',
        supplier_id: '',
        user_id: '',
        code: '',
        amount: '',
        payment_method: '',
        reference: '',
        status: 'pending',
        due_date: '',
        payment_date: null,
        notes: ''
      };
    },
    async handlePaymentSubmit(paymentData) {
      const response = await supplierPaymentService.createSupplierPayment(paymentData);
      if (response) {
        this.closePaymentModal();
        notify.alert.success('Payment created successfully');
      }
    },
  },

  computed: {
    headerMain() {
      return {
        ...this.purchase,
        store: this.purchase.supplier
      }
    },
    getConfirmTitle() {
      switch (this.purchaseStatus) {
        case 'submitted':
          return 'Submit Supplier Purchase'
        case 'pending':
          return 'Edit Supplier Purchase'
        case 'complete':
          return 'Approve Supplier Purchase'
      }
    },
  },

  created: async function () {
    this.purchase = await purchaseService.getPurchase(this.$route.params.id)
    await this.getPurchaseDetails()
  },

  watch: {
    'purchaseDetails.price': function (newPrice) {
      if (newPrice && this.addProductMode === 'add') {
        if (this.selectedProduct.category?.sale_margin_percentage) {
          const margin = this.selectedProduct.category.sale_margin_percentage
          this.purchaseDetails.sell = Math.ceil(((newPrice * (margin/100)) + parseFloat(newPrice)))
        } else {
          const defaultMargin = 20
          this.purchaseDetails.sell = Math.ceil(((newPrice * (defaultMargin/100)) + parseFloat(newPrice)))
        }
      }
    },
    addProductModal: function () {
      if (!this.addProductModal) {
        this.purchaseDetails = {
          purchase_id: this.$route.params.id,
          product_id: '',
          quantity: 1,
          price: '',
          sell: '',
          expiry_date: '',
          comment: ''
        }
        this.selectedProduct = { product: {}, category: {} }
      }
    }
  }

}
</script>

<style scoped>
</style>
